import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, apiPostJson } from './client'
import type { SettingsTab } from './settingsAgentInstall'

/**
 * Settings -> INTEGRARE -> Webhooks (nou, numai in v4; cerinta CEC R12).
 *
 *   GET  /settings/webhooks                               ecranul
 *   POST /settings/webhooks                               abonament nou -> `secret`, o singura data
 *   POST /settings/webhooks/{id}                          schimbat
 *   POST /settings/webhooks/{id}/delete                   sters
 *   POST /settings/webhooks/{id}/rotate                   secret nou -> `secret`, o singura data
 *   POST /settings/webhooks/{id}/test                     eveniment de proba in coada
 *   GET  /settings/webhooks/{id}/deliveries               jurnalul livrarilor
 *   GET  /settings/webhooks/{id}/deliveries/{d}           o livrare: payload + incercari
 *   POST /settings/webhooks/{id}/deliveries/{d}/resend    trimisa din nou
 *
 * SECRETUL NU VINE NICIODATA DINTR-UN GET: numai raspunsul care l-a facut il
 * poarta. Antetele suplimentare vin numai cu NUMELE - valorile pot fi tokenuri.
 */

export type WebhookEventGroup = { group: string; events: Array<{ event: string; label: string }> }

export type Webhook = {
  id: number
  /** `provider` = toate firmele furnizorului (numai administratorii). */
  scope: 'company' | 'provider'
  url: string
  description: string
  events: string[]
  /** Ultimele 4 caractere ale secretului, ca omul sa-l recunoasca. */
  secretHint: string
  /** Cat mai semneaza secretul vechi dupa o rotatie; gol = nu e rotatie in curs. */
  previousSecretUntil: string
  headerNames: string[]
  active: boolean
  consecutiveFailures: number
  /** Motivul opririi automate; gol = nu a fost oprit automat. */
  disabledReason: string
  disabledAt: string
  lastDeliveryAt: string
  lastStatus: string
  createdAt: string
}

export type WebhooksShell = {
  pageTitle: string
  moduleUrl: string
  tabs: SettingsTab[]
  /** false = migrarea n-a rulat pe baza asta. */
  ready: boolean
  /** false = DEFUSE_CRYPT_PASS lipseste; secretele nu se pot pastra. */
  cryptoReady: boolean
  canWrite: boolean
  canProviderWide: boolean
  eventCatalog: WebhookEventGroup[]
  webhooks: Webhook[]
}

export type WebhookInput = {
  url: string
  description: string
  events: string[]
  /** Valoare goala la editare = ramane cea de acum. */
  headers: Array<{ name: string; value: string }>
  active: boolean
  scope?: 'company' | 'provider'
}

export type WebhookDelivery = {
  id: number
  event: string
  eventId: string
  /** pending / sending / success / failed / cancelled */
  status: string
  attempts: number
  nextAttemptAt: string
  lastAttemptAt: string
  responseCode: number | null
  durationMs: number | null
  lastError: string
  test: boolean
  createdAt: string
  completedAt: string
}

export type WebhookAttempt = {
  attempt: number
  startedAt: string
  url: string
  requestHeaders: string
  resolvedIp: string
  responseCode: number | null
  responseBody: string
  durationMs: number | null
  error: string
}

export const webhooksShellQuery = queryOptions({
  queryKey: ['settings', 'webhooks'],
  queryFn: () => apiGet<WebhooksShell>('/settings/webhooks'),
})

export const webhookDeliveriesQuery = (id: number) =>
  queryOptions({
    queryKey: ['settings', 'webhooks', id, 'deliveries'],
    queryFn: () => apiGet<{ webhook: Webhook; deliveries: WebhookDelivery[] }>(`/settings/webhooks/${id}/deliveries`),
  })

export function getWebhookDelivery(id: number, delivery: number) {
  return apiGet<{ delivery: WebhookDelivery & { payload: string }; attempts: WebhookAttempt[] }>(
    `/settings/webhooks/${id}/deliveries/${delivery}`,
  )
}

export function createWebhook(input: WebhookInput) {
  return apiPostJson<WebhooksShell & { saved: boolean; id: number; secret: string }>('/settings/webhooks', input)
}

export function updateWebhook(id: number, input: WebhookInput) {
  return apiPostJson<WebhooksShell & { saved: boolean }>(`/settings/webhooks/${id}`, input)
}

export function deleteWebhook(id: number) {
  return apiPost<WebhooksShell & { saved: boolean }>(`/settings/webhooks/${id}/delete`, {})
}

export function rotateWebhookSecret(id: number) {
  return apiPost<WebhooksShell & { saved: boolean; id: number; secret: string }>(`/settings/webhooks/${id}/rotate`, {})
}

export function sendWebhookTest(id: number) {
  return apiPost<{ queued: boolean; deliveryId: number }>(`/settings/webhooks/${id}/test`, {})
}

export function resendWebhookDelivery(id: number, delivery: number) {
  return apiPost<{ queued: boolean; deliveryId: number }>(`/settings/webhooks/${id}/deliveries/${delivery}/resend`, {})
}
